import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { COMMON_STYLES } from '../utils/constants';

// Styles locaux
const STYLES = {
  pageLoading: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' },
  loadingContent: { textAlign: 'center' },
  loadingIcon: { fontSize: '48px', marginBottom: '16px' },
  loadingText: { color: '#666' },
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '40px 20px' },
  container: { maxWidth: '800px', margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: '40px', color: 'white' },
  headerTitle: { fontSize: '32px', margin: '0 0 8px 0', fontWeight: '700' },
  headerSubtitle: { opacity: 0.8, margin: 0 },
  card: { background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '16px' },
  cardTitle: { margin: '0 0 16px 0', fontSize: '16px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' },
  badge: (color) => ({ display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '600', color: 'white', background: color }),
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' },
  infoLabel: { color: '#666' },
  infoValue: (highlight) => ({ fontWeight: highlight ? '600' : '400', color: highlight ? '#333' : '#666', fontFamily: 'monospace' }),
  errorBox: { ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_DANGER, textAlign: 'center' },
  errorMessage: { margin: '8px 0 0 0' },
  statusRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' },
  statusIcon: { fontSize: '48px' },
  versionText: { margin: '8px 0 0 0', color: '#666' },
  serviceRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  serviceIcon: { fontSize: '24px' },
  serviceNote: (color) => ({ color, margin: '8px 0 0', fontSize: '13px' }),
  unavailableNote: { color: '#666', margin: 0 },
  pkgRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' },
  pkgName: { color: '#666' },
  pkgVersion: { display: 'flex', alignItems: 'center', gap: '8px' },
  pkgVersionText: { fontFamily: 'monospace', color: '#333' },
  updateBadge: (hasUpdate) => ({ background: hasUpdate ? '#dc3545' : '#28a745', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }),
  warnBadge: { background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  naBadge: { background: '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '10px' },
  footer: { textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '40px' },
  footerLink: { color: 'rgba(255,255,255,0.8)', textDecoration: 'underline' }
};

/**
 * Page de statut accessible sans authentification
 * Affiche l'état de santé du système en temps réel
 */
const StatusPage = () => {
  const [health, setHealth] = useState(null);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchHealth = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PLANNING_HEALTH);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setHealth(data);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.VERSIONS);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVersions(data.data);
        }
      }
    } catch (err) {
      console.warn('Impossible de charger les versions:', err);
    }
  };

  useEffect(() => {
    fetchHealth();
    fetchVersions();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'OK': case 'connected': return '#28a745';
      case 'DEGRADED': case 'timeout': return '#ffc107';
      case 'ERROR': case 'error': case 'unavailable': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const StatusBadge = ({ status }) => <span style={STYLES.badge(getStatusColor(status))}>{status}</span>;

  const Card = ({ title, icon, children }) => (
    <div style={STYLES.card}>
      <h3 style={STYLES.cardTitle}><span>{icon}</span>{title}</h3>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, highlight }) => (
    <div style={STYLES.infoRow}>
      <span style={STYLES.infoLabel}>{label}</span>
      <span style={STYLES.infoValue(highlight)}>{value}</span>
    </div>
  );

  if (loading) {
    return (
      <div style={STYLES.pageLoading}>
        <div style={STYLES.loadingContent}>
          <div style={STYLES.loadingIcon}>⏳</div>
          <p style={STYLES.loadingText}>Chargement du statut...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={STYLES.page}>
      <div style={STYLES.container}>
        {/* Header */}
        <div style={STYLES.header}>
          <h1 style={STYLES.headerTitle}>Renfort Connect Status</h1>
          <p style={STYLES.headerSubtitle}>Surveillance en temps réel</p>
        </div>

        {/* Statut global */}
        <Card title="Statut Global" icon="🎯">
          {error ? (
            <div style={STYLES.errorBox}>
              <strong>Erreur de connexion</strong>
              <p style={STYLES.errorMessage}>{error}</p>
            </div>
          ) : health ? (
            <>
              <div style={STYLES.statusRow}>
                <span style={STYLES.statusIcon}>{health.status === 'OK' ? '✅' : health.status === 'DEGRADED' ? '⚠️' : '❌'}</span>
                <div>
                  <StatusBadge status={health.status} />
                  <p style={STYLES.versionText}>Version {health.version}</p>
                </div>
              </div>
              {health.environment && <InfoRow label="Environnement" value={health.environment} />}
              {health.buildDate && health.buildDate !== 'unknown' && <InfoRow label="Build" value={new Date(health.buildDate).toLocaleString('fr-FR')} />}
              {health.uptime && <InfoRow label="Uptime Backend" value={health.uptime} highlight />}
              {health.startedAt && <InfoRow label="Backend démarré" value={new Date(health.startedAt).toLocaleString('fr-FR')} />}
              {window._env_?.FRONTEND_STARTED_AT && (
                <InfoRow label="Frontend démarré" value={new Date(window._env_.FRONTEND_STARTED_AT).toLocaleString('fr-FR')} />
              )}
            </>
          ) : null}
        </Card>

        {health && (
          <>
            {/* Base de données - affiché seulement si les données détaillées sont disponibles */}
            {health.database && (
              <Card title="Base de données" icon="🗄️">
                <div style={STYLES.serviceRow}>
                  <span style={STYLES.serviceIcon}>{health.database?.status === 'connected' ? '🟢' : '🔴'}</span>
                  <StatusBadge status={health.database?.status || 'unknown'} />
                </div>
                {health.database?.responseTimeMs != null && (
                  <InfoRow label="Temps de réponse" value={`${health.database.responseTimeMs}ms`} highlight />
                )}
              </Card>
            )}

            {/* PaddleOCR - affiché seulement si les données sont disponibles */}
            {health.paddleocr && (
              <Card title="Service OCR (PaddleOCR)" icon="🔍">
                <div style={STYLES.serviceRow}>
                  <span style={STYLES.serviceIcon}>
                    {health.paddleocr?.status === 'connected' ? '🟢' : health.paddleocr?.status === 'unavailable' ? '🔴' : health.paddleocr?.status === 'timeout' ? '🟡' : '⚪'}
                  </span>
                  <StatusBadge status={health.paddleocr?.status || 'unknown'} />
                </div>
                {health.paddleocr?.responseTimeMs != null && health.paddleocr?.status === 'connected' && (
                  <>
                    <InfoRow label="Temps de réponse" value={`${health.paddleocr.responseTimeMs}ms`} highlight />
                    <InfoRow label="Version" value={health.paddleocr.version || 'N/A'} />
                  </>
                )}
                {health.paddleocr?.status === 'unavailable' && (
                  <p style={STYLES.serviceNote('#dc3545')}>Le conteneur PaddleOCR n'est pas démarré.<br/><code style={{ fontSize: '11px' }}>docker-compose up -d paddleocr</code></p>
                )}
                {health.paddleocr?.status === 'timeout' && (
                  <p style={STYLES.serviceNote('#ffc107')}>Le service met trop de temps à répondre (timeout 3s)</p>
                )}
              </Card>
            )}

            {/* WebSocket / Collaboration - affiché seulement si disponible */}
            {health.websocket && (
              <Card title="Collaboration temps réel" icon="🔌">
                <InfoRow label="Utilisateurs connectés" value={health.websocket.activeUsers || 0} highlight />
                <InfoRow label="Verrous actifs" value={health.websocket.activeLocks || 0} />
              </Card>
            )}

            {/* Mémoire - affiché seulement si disponible */}
            {health.memory && (
              <Card title="Ressources système" icon="💾">
                <InfoRow label="Heap utilisé" value={health.memory?.heapUsed} />
                <InfoRow label="Heap total" value={health.memory?.heapTotal} />
                <InfoRow label="RSS" value={health.memory?.rss} />
                <InfoRow label="Système libre" value={health.memory?.systemFree} />
              </Card>
            )}

            {/* Infos système - affiché seulement si disponible */}
            {health.system && (
              <Card title="Informations serveur" icon="🖥️">
                <InfoRow label="Node.js" value={health.system?.nodeVersion} />
                <InfoRow label="Plateforme" value={health.system?.platform} />
                <InfoRow label="Architecture" value={health.system?.arch} />
                <InfoRow label="CPUs" value={health.system?.cpus} />
              </Card>
            )}
          </>
        )}

        {/* Versions des packages */}
        {versions && (
          <>
            <Card title="Versions Application" icon="📦">
              <InfoRow label="Backend" value={versions.application?.backendVersion} highlight />
              <InfoRow label="Frontend" value={versions.application?.frontendVersion} highlight />
              <InfoRow label="Image Docker" value={versions.runtime?.imageTag} />
              <InfoRow label="Build" value={versions.runtime?.buildDate !== 'unknown' ? versions.runtime.buildDate : 'Local'} />
            </Card>

            {versions.docker && (
              <Card title="Infrastructure Docker" icon="🐳">
                {['node', 'postgres', 'redis', 'nginx', 'paddleocr'].map((service) => {
                  const info = versions.docker[service];
                  if (!info || typeof info !== 'object') return null;
                  const labels = { node: 'Node.js (Backend)', postgres: 'PostgreSQL', redis: 'Redis', nginx: 'Nginx (Frontend)', paddleocr: 'PaddleOCR' };
                  return (
                    <div key={service} style={STYLES.pkgRow}>
                      <span style={STYLES.pkgName}>{labels[service]}</span>
                      <span style={STYLES.pkgVersion}>
                        <span style={STYLES.pkgVersionText}>{info.currentVersion || info.current || 'Non démarré'}</span>
                        {info.hasUpdate && info.latestVersion && <span style={STYLES.updateBadge(true)}>MAJ → {info.latestVersion}</span>}
                        {!info.hasUpdate && info.latestVersion && <span style={STYLES.updateBadge(false)}>OK</span>}
                        {info.note && !info.latestVersion && <span style={STYLES.naBadge}>N/A</span>}
                      </span>
                    </div>
                  );
                })}
                <InfoRow label="Environnement" value={versions.docker.environment} />
              </Card>
            )}

            {versions.monitoring && (
              <Card title="Stack Monitoring" icon="📊">
                <InfoRow label="Prometheus" value={versions.monitoring.prometheus || 'Non démarré'} highlight />
                <InfoRow label="Grafana" value={versions.monitoring.grafana || 'Non démarré'} highlight />
                <InfoRow label="Loki" value={versions.monitoring.loki || 'Non démarré'} />
                <InfoRow label="Alertmanager" value={versions.monitoring.alertmanager || 'Non démarré'} />
              </Card>
            )}

            {versions.backend && (
              <Card title="Packages Backend" icon="⚙️">
                {Object.entries(versions.backend).map(([pkg, info]) => (
                  <div key={pkg} style={STYLES.pkgRow}>
                    <span style={STYLES.pkgName}>{pkg}</span>
                    <span style={STYLES.pkgVersion}>
                      <span style={STYLES.pkgVersionText}>{typeof info === 'string' ? info : info.current}</span>
                      {info.hasUpdate && <span style={STYLES.warnBadge}>→ {info.latest}</span>}
                    </span>
                  </div>
                ))}
              </Card>
            )}

            {versions.frontend && (
              <Card title="Packages Frontend" icon="🎨">
                {Object.entries(versions.frontend).map(([pkg, info]) => (
                  <div key={pkg} style={STYLES.pkgRow}>
                    <span style={STYLES.pkgName}>{pkg}</span>
                    <span style={STYLES.pkgVersion}>
                      <span style={STYLES.pkgVersionText}>{typeof info === 'string' ? info : info.current}</span>
                      {info.hasUpdate && <span style={STYLES.warnBadge}>→ {info.latest}</span>}
                    </span>
                  </div>
                ))}
              </Card>
            )}

            {versions.paddleocr && (
              <Card title="PaddleOCR (Python)" icon="🔍">
                <InfoRow label="Service" value={versions.paddleocr.serviceVersion} highlight />
                <InfoRow label="Python" value={versions.paddleocr.pythonVersion} highlight />
                {versions.paddleocr.packages && Object.entries(versions.paddleocr.packages).map(([pkg, version]) => (
                  <InfoRow key={pkg} label={pkg} value={version} />
                ))}
              </Card>
            )}
          </>
        )}

        {/* Footer */}
        <div style={STYLES.footer}>
          <p>
            Dernière mise à jour : {lastUpdate?.toLocaleTimeString('fr-FR') || '-'}
            <br />
            Rafraîchissement automatique toutes les 30 secondes
          </p>
          <p style={{ marginTop: '16px' }}>
            <a href="/" style={STYLES.footerLink}>← Retour à l'application</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatusPage;
